/**
 * Searchable IANA timezone picker.
 *
 * Selection alone does NOT save — parent should hold draft state and
 * expose an Apply button. `onChange` only updates the draft value.
 *
 * `timezones` — optional preloaded list; if omitted the component fetches
 * from `listEndpoint` once (publisher: /publisher/timezones, admin: /admin/timezones).
 */
import { useEffect, useMemo, useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { CaretDown, Check, CircleNotch } from "@phosphor-icons/react";

export function detectBrowserTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    // Guard against rare legacy abbreviations leaking into storage.
    const abbrev = {
      EST: "America/New_York", EDT: "America/New_York",
      CST: "America/Chicago", CDT: "America/Chicago",
      MST: "America/Denver", MDT: "America/Denver",
      PST: "America/Los_Angeles", PDT: "America/Los_Angeles",
    };
    return abbrev[tz] || tz;
  } catch {
    return "UTC";
  }
}

/** cmdk lowercases CommandItem values — map back to canonical IANA casing. */
function resolveCanonical(selected, timezones) {
  if (!selected) return selected;
  const hit = (timezones || []).find((z) => z.toLowerCase() === String(selected).toLowerCase());
  return hit || selected;
}

export default function TimezoneSelect({
  value,
  onChange,
  timezones: timezonesProp,
  listEndpoint = "/publisher/timezones",
  placeholder = "Select timezone…",
  disabled = false,
  className = "",
  testId = "timezone-select",
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(!timezonesProp);
  const [timezones, setTimezones] = useState(timezonesProp || []);

  useEffect(() => {
    if (timezonesProp) {
      setTimezones(timezonesProp);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await API.get(listEndpoint);
        if (!cancelled) setTimezones(data?.timezones || []);
      } catch {
        if (!cancelled) setTimezones(["UTC", "America/New_York"]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [timezonesProp, listEndpoint]);

  const current = value || "UTC";
  const label = useMemo(() => current, [current]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || loading}
          className={`w-full justify-between font-mono text-sm h-10 ${className}`}
          data-testid={testId}
        >
          <span className="truncate">{loading ? "Loading…" : (label || placeholder)}</span>
          {loading ? (
            <CircleNotch size={14} className="ml-2 shrink-0 animate-spin opacity-50" />
          ) : (
            <CaretDown size={14} className="ml-2 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search timezone…" data-testid={`${testId}-search`} />
          <CommandList>
            <CommandEmpty>No timezone found.</CommandEmpty>
            <CommandGroup>
              {timezones.map((tz) => (
                <CommandItem
                  key={tz}
                  value={tz}
                  onSelect={(selected) => {
                    // Prefer list item casing; fall back to resolving cmdk's
                    // lowercased `selected` against the loaded list.
                    const next = resolveCanonical(selected || tz, timezones);
                    onChange?.(next);
                    setOpen(false);
                  }}
                  data-testid={`${testId}-option-${tz}`}
                >
                  <Check
                    size={14}
                    className={`mr-2 shrink-0 ${current.toLowerCase() === tz.toLowerCase() ? "opacity-100" : "opacity-0"}`}
                  />
                  <span className="font-mono text-xs">{tz}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
